import React from "react";
import { Document, Page, Text, View, Image, Font } from "@react-pdf/renderer";
import { styles } from "./style";
import moment from "moment";
import { decrypt } from "../../utils";

const getWeekKey = (date) => {
  return moment(date).startOf("isoWeek").format("YYYY-MM-DD");
};

// Helper function to group tasks by week and client
const groupTasksByWeek = (tasks) => {
  const grouped = {};

  tasks?.forEach((task) => {
    const weekKey = getWeekKey(task?.dateTime);
    const clientId = task?.client._id;
    const groupKey = `${weekKey}_${clientId}`;

    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        weekStart: weekKey,
        client: task?.client,
        facility: task?.facility,
        levelofneed: decrypt(task?.client?.form1)?.levelofneed,
        placeOfService: task?.facility?.address,
        zipcode: task?.facility?.zipcode,
        providerLicense: task?.facility?.providerLicense,
        tasks: {
          Monday: [],
          Tuesday: [],
          Wednesday: [],
          Thursday: [],
          Friday: [],
        },
      };
    }

    const dayOfWeek = moment(task.dateTime).format("dddd");
    if (
      ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].includes(
        dayOfWeek
      )
    ) {
      grouped[groupKey].tasks[dayOfWeek].push(task);
    }
  });

  return Object.values(grouped);
};

export const SafeImage = ({ src, imageMap, style, fallback = "N/A", alt }) => {
  if (!src) return <Text>{fallback}</Text>;
  const finalSrc = imageMap?.[src] || src;
  return <Image src={finalSrc} style={style} alt={alt} cache={false} />;
};

const Page1 = ({ weeklyData, dateRange, allSignature, imageMap }) => {
  // const reportDate = `(${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")})`;
  const weekStart = moment(weeklyData.weekStart);
  const weekEnd = moment(weeklyData.weekStart).add(4, "days"); // Friday is 4 days after Monday
  const reportDate = `(${weekStart.format("MM/DD/YYYY")} - ${weekEnd.format(
    "MM/DD/YYYY"
  )})`;

  const getSignRecord = (task) => {
    if (!task) return null;
    return allSignature?.find(({ staff, admin }) => {
      return (
        (staff && staff === task?.startedBy?._id) ||
        (admin && admin === task?.startedBy?._id)
      );
    });
  };

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.bottomLogo}>
        <Image src="/assets/images/logo2.png" style={styles.logo} alt="logo" />
      </View>
      <View style={styles.header}>
        <Text style={styles.reportTitle}>Form 8615</Text>
        <Text style={styles.reportPeriod}>{reportDate}</Text>
      </View>

      <View style={styles.texasLogoRow}>
        <Image
          src="/assets/images/texas-logo.png"
          style={styles.texasLogo}
          alt="logo"
        />
        <View>
          <Text style={styles.formNumber}>Form 8615</Text>
          <Text
            style={styles.formNumber}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} / ${totalPages}`
            }
          ></Text>
        </View>
      </View>

      <Text style={[styles.sectionTitle]}>
        Home and Community-based Services and Texas Home Living Services
      </Text>
      <Text style={[styles.sectionTitle, { marginBottom: 10, fontSize: 12 }]}>
        On-Site and Off-Site Individual Skills and Socialization Service
        Delivery Log
      </Text>

      <View
        style={[styles.infoRow, { borderTop: "1px solid #000", marginTop: 15 }]}
      >
        <View style={[styles.infoCell, { width: "35%" }]}>
          <Text style={styles.label}>Individual Name (First, Last)</Text>
          <Text>{decrypt(weeklyData?.client?.name) || "N/A"}</Text>
        </View>
        <View style={[styles.infoCell, { width: "35%" }]}>
          <Text style={styles.label}>Place of Service(s)</Text>
          <Text>
            {weeklyData?.placeOfService || "N/A"},{" "}
            {weeklyData?.zipcode || "N/A"}
          </Text>
        </View>
        <View style={[styles.infoCell, { width: "30%", borderRight: "none" }]}>
          <Text style={styles.label}>Level of Need</Text>
          <Text>{weeklyData?.levelofneed || "N/A"}</Text>
        </View>
      </View>

      <View style={[styles.infoRow, { borderTop: "none" }]}>
        <View style={[styles.infoCell, { width: "50%" }]}>
          <Text style={styles.label}>
            Name of Individualized Skills and Socialization Provider
          </Text>
          <Text>{weeklyData?.facility?.name || "N/A"}</Text>
        </View>
        <View style={[styles.infoCell, { width: "50%", borderRight: "none" }]}>
          <Text style={styles.label}>
            Individualized Skills and Socialization Provider License No
          </Text>
          <Text>{weeklyData?.providerLicense || "N/A"}</Text>
        </View>
      </View>

      {/* On-Site Schedule Table */}
      <View style={styles.table}>
        <View style={[styles.tableRow, styles.tableHeader]}>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]?.map(
            (day, idx) => (
              <Text
                key={day}
                style={[
                  styles.tableCell,
                  {
                    width: "20%",
                    textAlign: "center",
                    borderRight: idx === 4 ? "none" : "1px solid #000",
                  },
                ]}
              >
                {day} (On-site)
              </Text>
            )
          )}
        </View>

        <View style={styles.tableRow}>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]?.map(
            (day, idx) => {
              const tasksForDay =
                weeklyData?.tasks[day]?.filter((t) => t.site === "On-Site") ||
                [];

              // If no tasks, show empty structure
              if (tasksForDay?.length === 0) {
                return (
                  <View
                    key={day}
                    style={[
                      styles.tableCell,
                      {
                        width: "20%",
                        padding: 0,
                        borderRight: idx === 4 ? "none" : "1px solid #000",
                      },
                    ]}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        // borderTop: "1px solid #000",
                      }}
                    >
                      <View
                        style={[
                          styles.timeCell,
                          { borderRight: "1px solid #000", padding: 3 },
                        ]}
                      >
                        <Text style={styles.label}>Time In</Text>
                        <Text>-</Text>
                      </View>
                      <View style={[styles.timeCell, { padding: 3 }]}>
                        <Text style={styles.label}>Time Out</Text>
                        <Text>-</Text>{" "}
                      </View>
                    </View>

                    <View style={styles.tableCellStyle}>
                      <Text style={styles.label}>Name of Service Provider</Text>
                      <Text>-</Text>
                    </View>

                    <View style={styles.tableCellStyle}>
                      <Text style={styles.label}>Signature</Text>
                      <Text>-</Text>
                    </View>
                  </View>
                );
              }

              // If tasks exist, show them
              return (
                <View
                  key={day}
                  style={[
                    styles.tableCell,
                    {
                      width: "20%",
                      padding: 0,
                      borderRight: idx === 4 ? "none" : "1px solid #000",
                    },
                  ]}
                >
                  {tasksForDay?.map((task, tIdx) => {
                    const signRecord = getSignRecord(task);
                    return (
                      <View
                        key={tIdx}
                        style={{
                          borderBottom:
                            tIdx !== tasksForDay.length - 1
                              ? "1px solid #000"
                              : "none",
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            // borderTop: "1px solid #000",
                          }}
                        >
                          <View
                            style={[
                              styles.timeCell,
                              { borderRight: "1px solid #000", padding: 3 },
                            ]}
                          >
                            <Text style={styles.label}>Time In</Text>
                            <Text>
                              {task?.timeIn
                                ? moment(task?.timeIn, "HH:mm").format(
                                    "hh:mm A"
                                  )
                                : ""}
                            </Text>
                          </View>
                          <View style={[styles.timeCell, { padding: 3 }]}>
                            <Text style={styles.label}>Time Out</Text>
                            <Text>
                              {task?.timeOut
                                ? moment(task?.timeOut, "HH:mm").format(
                                    "hh:mm A"
                                  )
                                : ""}
                            </Text>
                          </View>
                        </View>

                        <View style={styles.tableCellStyle}>
                          <Text style={styles.label}>
                            Name of Service Provider
                          </Text>
                          <Text>{task?.facility?.name || ""}</Text>
                        </View>

                        <View style={styles.tableCellStyle}>
                          <Text style={styles.label}>Signature</Text>
                          {signRecord?.signature ? (
                            <SafeImage
                              src={
                                decrypt(signRecord?.signature) ||
                                signRecord?.signature
                              }
                              style={{ width: 100 }}
                              alt="Signature"
                              imageMap={imageMap}
                            />
                          ) : signRecord?.signatureImage ? (
                            <SafeImage
                              src={
                                decrypt(signRecord?.signatureImage) ||
                                signRecord?.signatureImage
                              }
                              style={{ width: 100 }}
                              alt="Signature"
                              imageMap={imageMap}
                            />
                          ) : signRecord?.signatureText ? (
                            <Text style={styles.sigText}>
                              {signRecord?.signatureText || "N/A"}
                            </Text>
                          ) : (
                            <Text>N/A</Text>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              );
            }
          )}
        </View>
      </View>

      {/* Off-Site Schedule Table */}
      <View style={[styles.table, { marginTop: 10 }]}>
        <View style={[styles.tableRow, styles.tableHeader]}>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]?.map(
            (day, idx) => (
              <Text
                key={day}
                style={[
                  styles.tableCell,
                  {
                    width: "20%",
                    textAlign: "center",
                    borderRight: idx === 4 ? "none" : "1px solid #000",
                  },
                ]}
              >
                {day} (Off-site)
              </Text>
            )
          )}
        </View>

        <View style={styles.tableRow}>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]?.map(
            (day, idx) => {
              const tasksForDay =
                weeklyData?.tasks[day]?.filter((t) => t.site === "Off-Site") ||
                [];
              const getRatio = (level) => {
                if (
                  level === "LON 1 - Intermittent" ||
                  level === "LON 5 - Limited"
                ) {
                  return "1:8";
                }
                return "1:2";
              };

              // If no tasks, show empty structure
              if (tasksForDay?.length === 0) {
                return (
                  <View
                    key={day}
                    style={[
                      styles.tableCell,
                      {
                        width: "20%",
                        padding: 0,
                        borderRight: idx === 4 ? "none" : "1px solid #000",
                      },
                    ]}
                  >
                    <View
                      style={{
                        borderBottom: "1px solid #000",
                        padding: 2,
                        // borderTop: "1px solid #000",
                      }}
                    >
                      <Text style={styles.label}>Ratio:</Text>
                      <Text>-</Text>
                    </View>
                    <View
                      style={{
                        borderBottom: "1px solid #000",
                        padding: 2,
                      }}
                    >
                      <Text style={styles.label}>Staff:</Text>
                      <Text>-</Text>
                    </View>
                    <View style={{ padding: 2 }}>
                      <Text style={styles.label}>Individuals:</Text>
                      <Text>-</Text>
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        borderTop: "1px solid #000",
                      }}
                    >
                      <View
                        style={[
                          styles.timeCell,
                          { borderRight: "1px solid #000", padding: 3 },
                        ]}
                      >
                        <Text style={styles.label}>Time In</Text>
                        <Text>-</Text>
                      </View>
                      <View style={[styles.timeCell, { padding: 3 }]}>
                        <Text style={styles.label}>Time Out</Text>
                        <Text>-</Text>
                      </View>
                    </View>
                    <View style={styles.tableCellStyle}>
                      <Text style={styles.label}>Name of Service Provider</Text>
                      <Text>-</Text>
                    </View>
                    <View style={styles.tableCellStyle}>
                      <Text style={styles.label}>Signature</Text>
                      <Text>-</Text>
                    </View>
                  </View>
                );
              }

              // If tasks exist, show them
              return (
                <View
                  key={day}
                  style={[
                    styles.tableCell,
                    {
                      width: "20%",
                      padding: 0,
                      borderRight: idx === 4 ? "none" : "1px solid #000",
                    },
                  ]}
                >
                  {tasksForDay?.map((task, tIdx) => {
                    const signRecord = getSignRecord(task);

                    return (
                      <View
                        key={tIdx}
                        style={{
                          borderBottom:
                            tIdx !== tasksForDay.length - 1
                              ? "1px solid #000"
                              : "none",
                        }}
                      >
                        <View
                          style={{
                            borderBottom: "1px solid #000",
                            padding: 2,
                            // borderTop: "1px solid #000",
                          }}
                        >
                          <Text style={styles.label}>Ratio:</Text>
                          <Text>
                            {task
                              ? weeklyData?.levelofneed
                                ? getRatio(weeklyData?.levelofneed)
                                : "N/A"
                              : ""}
                          </Text>
                        </View>
                        <View
                          style={{
                            borderBottom: "1px solid #000",
                            padding: 2,
                          }}
                        >
                          <Text style={styles.label}>Staff:</Text>
                          <Text>{task?.startedBy?.name || ""}</Text>
                        </View>
                        <View style={{ padding: 2 }}>
                          <Text style={styles.label}>Individuals:</Text>
                          <Text>
                            {task ? decrypt(task.client?.name) || "" : ""}
                          </Text>
                        </View>
                        <View
                          style={{
                            flexDirection: "row",
                            borderTop: "1px solid #000",
                          }}
                        >
                          <View
                            style={[
                              styles.timeCell,
                              { borderRight: "1px solid #000", padding: 3 },
                            ]}
                          >
                            <Text style={styles.label}>Time In</Text>
                            <Text>
                              {task?.timeIn
                                ? moment(task.timeIn, "HH:mm").format("hh:mm A")
                                : ""}
                            </Text>
                          </View>
                          <View style={[styles.timeCell, { padding: 3 }]}>
                            <Text style={styles.label}>Time Out</Text>
                            <Text>
                              {task?.timeOut
                                ? moment(task.timeOut, "HH:mm").format(
                                    "hh:mm A"
                                  )
                                : ""}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.tableCellStyle}>
                          <Text style={styles.label}>
                            Name of Service Provider
                          </Text>
                          <Text>{task?.facility?.name || ""}</Text>
                        </View>
                        <View style={styles.tableCellStyle}>
                          <Text style={styles.label}>Signature</Text>
                          {task && signRecord?.signature ? (
                            <SafeImage
                              src={
                                decrypt(signRecord?.signature) ||
                                signRecord?.signature
                              }
                              style={{ width: 100 }}
                              alt="signature"
                              imageMap={imageMap}
                            />
                          ) : task && signRecord?.signatureImage ? (
                            <SafeImage
                              src={
                                decrypt(signRecord?.signatureImage) ||
                                signRecord?.signatureImage
                              }
                              style={{ width: 100 }}
                              alt="signature"
                              imageMap={imageMap}
                            />
                          ) : task && signRecord?.signatureText ? (
                            <Text style={styles.sigText}>
                              {signRecord?.signatureText || "N/A"}
                            </Text>
                          ) : (
                            <Text>N/A</Text>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              );
            }
          )}
        </View>
      </View>
    </Page>
  );
};

const Page2 = ({ weeklyData, dateRange, allSignature, imageMap }) => {
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

  const renderDayCell = (fieldName, day, isLast = false) => {
    const tasks = weeklyData.tasks[day] || [];

    // if (tasks.length === 0) {
    //   return (
    //     <Text style={[styles.dayCell, isLast && { borderRight: "none" }]}>
    //       -
    //     </Text>
    //   );
    // }

    return (
      <View
        style={[
          {
            flexDirection: "column",
            width: "14%",
            borderRight: "1px solid #000",
          },
        ]}
      >
        {tasks?.map((task, tIdx) => {
          const fieldData = task?.fieldsDone?.find(
            (item) => item?.field?.name === fieldName
          );
          if (!fieldData?.staff) return null;

          const staffId = fieldData.staff?._id || fieldData.staff; // ✅ Correct ID
          const signRecord = allSignature?.find(
            ({ staff: stf, admin }) =>
              String(stf) === String(staffId) ||
              String(admin) === String(staffId)
          );

          if (signRecord?.signatureInitials) {
            return (
              <SafeImage
                alt="signature"
                src={
                  decrypt(signRecord?.signatureInitials) ||
                  signRecord?.signatureInitials
                }
                style={[
                  styles.dayCell,
                  { marginBottom: 2, width: 100, borderRight: "none" },
                ]}
                key={tIdx}
                imageMap={imageMap}
              />
            );
          } else if (signRecord?.signatureInitialsText) {
            return (
              <Text
                key={tIdx}
                style={[
                  styles.dayCell,
                  {
                    fontFamily: "Island Moments",
                    fontSize: 16,
                    textAlign: "center",
                    marginBottom: 2,
                    borderRight: "none",
                  },
                ]}
              >
                {signRecord?.signatureInitialsText}
              </Text>
            );
          } else {
            return (
              <Text
                key={tIdx}
                style={[
                  styles.dayCell,
                  {
                    marginBottom: 2,
                    borderRight: "none",
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                ]}
              >
                N/A
              </Text>
            );
          }
        })}
      </View>
    );
  };

  const getAllFieldsForCategory = (fieldNames) => {
    const uniqueFields = new Set();

    days?.forEach((day) => {
      const tasks = weeklyData?.tasks[day];
      if (tasks?.length > 0) {
        tasks?.forEach((task) => {
          task?.fieldsDone?.forEach((item) => {
            if (fieldNames.includes(item?.field?.name)) {
              uniqueFields.add(item?.field?.name);
            }
          });
        });
      }
    });

    return Array.from(uniqueFields);
  };

  // Render skill table section
  const SkillTable = ({ title, fieldNames }) => {
    // const uniqueFields = getAllFieldsForCategory(fieldNames);

    return (
      <View style={[styles.skillTable, { border: "1px solid #000" }]}>
        <View
          style={[
            styles.skillRow,
            styles.sectionHeader,
            { borderBottom: "1px solid #000", padding: 0 },
          ]}
        >
          <Text style={[styles.skillNameCell, { fontWeight: "bold" }]}>
            {title}
          </Text>
          {days?.map((day, idx) => (
            <Text
              key={day}
              style={[
                styles.dayCell,
                { fontWeight: "bold" },
                idx === 4 && { borderRight: "none" },
              ]}
            >
              {day}
            </Text>
          ))}
        </View>
        {fieldNames?.length > 0 ? (
          fieldNames?.map((fieldName, idx) => (
            <View key={idx} style={styles.skillRow}>
              <Text style={styles.skillNameCell}>{fieldName}</Text>
              {days?.map((day, dayIdx) =>
                renderDayCell(fieldName, day, dayIdx === 4)
              )}
            </View>
          ))
        ) : (
          <View style={styles.skillRow}>
            <Text style={styles.skillNameCell}></Text>
            {days?.map((day, idx) => (
              <Text
                key={day}
                style={[styles.dayCell, idx === 4 && { borderRight: "none" }]}
              >
                -
              </Text>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Skill categories configuration
  const skillCategories = [
    {
      title: "Socialization Skills",
      fields: [
        "Communication",
        "Socialization Skills Development",
        "Group Activity",
        "Volunteer or Employment Skills Development",
      ],
    },
    {
      title: "Self-Help Skills",
      fields: [
        "Personal Hygiene",
        "Eating",
        "Meal Preparation",
        "Cleaning",
        "Assistance with Medication ",
      ],
    },
    {
      title: "Adaptive Skills",
      fields: ["Ambulation and Mobility", "Reinforce Lessons"],
    },
    {
      title: "Implementation Plan Skill Development",
      fields: ["Other"],
    },
    {
      title: "Community Integration (for off-site only)",
      fields: [
        "Community Location (Describe location(s) in the comments)",
        "Transportation",
      ],
    },
  ];

  return (
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.bottomLogo}>
        <Image src="/assets/images/logo2.png" style={styles.logo} alt="logo" />
      </View>
      <View style={styles.header}>
        <Text style={styles.reportTitle}>Form 8615</Text>
      </View>

      <View style={[styles.texasLogoRow, { marginBottom: 5 }]}>
        <View></View>
        <View style={{ textAlign: "justify" }}>
          <Text style={styles.formNumber}>Form 8615</Text>
          <Text
            style={styles.formNumber}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} / ${totalPages}`
            }
          ></Text>
        </View>
      </View>

      <View style={{ marginTop: 3 }}>
        {/* Individual Info */}
        <View style={[styles.infoRow, { borderTop: "1px solid #000" }]}>
          <View style={[styles.infoCell, { width: "50%" }]}>
            <Text style={styles.label}>Individual Name (First, Last)</Text>
            <Text>{decrypt(weeklyData.client?.name) || "N/A"}</Text>
          </View>
          <View
            style={[styles.infoCell, { width: "50%", borderRight: "none" }]}
          >
            <Text style={styles.label}>
              Name of Individualized Skills and Socialization Provider
            </Text>
            <Text>{weeklyData.facility?.name || "N/A"}</Text>
          </View>
        </View>

        <Text style={{ fontSize: 8, marginTop: 10, marginBottom: 0 }}>
          Initial all areas in which you assisted the person:
        </Text>

        {/* Render all skill tables */}
        {skillCategories?.map((category, idx) => (
          <SkillTable
            key={idx}
            title={category.title}
            fieldNames={category.fields}
          />
        ))}

        {/* Community Locations Table */}
        <Text
          style={{
            fontSize: 9,
            fontWeight: "bold",
            marginTop: 10,
            marginBottom: 0,
          }}
        >
          Community Locations Visited and Special Events or Occurrences
        </Text>
        <View style={styles.communityTable}>
          <View style={[styles.communityRow, styles.tableHeader]}>
            <Text style={[styles.dateCell, { fontWeight: "bold" }]}>Date</Text>
            <Text style={[styles.staffCell, { fontWeight: "bold" }]}>
              Staff Initials
            </Text>
            <Text
              style={[
                styles.commentsCell,
                { fontWeight: "bold", borderRight: "none" },
              ]}
            >
              Comments
            </Text>
          </View>
          {days.map((day) => {
            const tasks = weeklyData?.tasks[day];
            return tasks?.map((task) =>
              task?.fieldsDone?.map(
                ({ checkedAt, _id, textField, field, staff, textEntries }) => {
                  const sig = allSignature?.find(
                    ({ staff: stf, admin }) =>
                      stf === staff?._id || admin === staff?._id
                  );
                  return (
                    field?.name ===
                      "Community Location (Describe location(s) in the comments)" &&
                    task?.site === "Off-Site" && (
                      <React.Fragment key={_id}>
                        <View style={styles.communityRow}>
                          <Text style={styles.dateCell}>
                            {checkedAt
                              ? moment(checkedAt).format("MM/DD/YYYY hh:mm A")
                              : "-"}
                          </Text>
                          {sig?.signatureInitials ? (
                            <View style={styles.staffCell}>
                              <SafeImage
                                src={
                                  decrypt(sig?.signatureInitials) ||
                                  sig?.signatureInitials
                                }
                                style={{ width: "100%" }}
                                alt="signature"
                                imageMap={imageMap}
                              />
                            </View>
                          ) : sig?.signatureInitialsText ? (
                            <Text
                              style={[
                                styles.staffCell,
                                {
                                  fontFamily: "Island Moments",
                                  fontSize: 18,
                                  textAlign: "center",
                                },
                              ]}
                            >
                              {sig?.signatureInitialsText || "N/A"}
                            </Text>
                          ) : (
                            <Text style={styles.staffCell}>N/A</Text>
                          )}
                          <Text
                            style={[
                              styles.commentsCell,
                              { borderRight: "none" },
                            ]}
                          >
                            {textField || "N/A"}
                          </Text>
                        </View>
                        {textEntries &&
                          textEntries?.length > 0 &&
                          textEntries.map((entry) => {
                            const signRecord = allSignature?.find(
                              ({ staff: stf, admin }) =>
                                stf === entry.staff || admin === entry.staff
                            );
                            return (
                              <View
                                key={entry?._id}
                                style={styles.communityRow}
                              >
                                <Text style={styles.dateCell}>
                                  {moment(entry?.checkedAt).format(
                                    "MM/DD/YYYY hh:mm A"
                                  )}
                                </Text>
                                {signRecord?.signatureInitials ? (
                                  <View style={styles.staffCell}>
                                    <SafeImage
                                      src={
                                        decrypt(
                                          signRecord?.signatureInitials
                                        ) || signRecord?.signatureInitials
                                      }
                                      style={{ width: "100%" }}
                                      alt="signature"
                                      imageMap={imageMap}
                                    />
                                  </View>
                                ) : signRecord?.signatureInitialsText ? (
                                  <Text
                                    style={[
                                      styles.staffCell,
                                      {
                                        fontFamily: "Island Moments",
                                        fontSize: 18,
                                        textAlign: "center",
                                      },
                                    ]}
                                  >
                                    {signRecord?.signatureInitialsText || "N/A"}
                                  </Text>
                                ) : (
                                  <Text style={styles.staffCell}>N/A</Text>
                                )}
                                <Text
                                  style={[
                                    styles.commentsCell,
                                    { borderRight: "none" },
                                  ]}
                                >
                                  {entry?.text || "N/A"}
                                </Text>
                              </View>
                            );
                          })}
                      </React.Fragment>
                    )
                  );
                }
              )
            );
          })}
        </View>
      </View>
    </Page>
  );
};

export const Form8615ReportPdf = ({
  data,
  dateRange,
  allSignature,
  imageMap,
}) => {
  // Group tasks by week and client
  const weeklyGroupedData = groupTasksByWeek(data);

  Font.register({
    family: "Helvetica",
    fonts: [{ src: "Helvetica" }],
  });

  Font.register({
    family: "Island Moments",
    fonts: [{ src: "/assets/fonts/IslandMoments-Regular.ttf" }],
  });

  // Check if data is empty or no weekly data exists
  if (!weeklyGroupedData || weeklyGroupedData.length === 0) {
    return (
      <Document>
        <Page size="A4" style={styles.emptyPage}>
          <View style={styles.bottomLogo}>
            <Image
              src="/assets/images/logo2.png"
              style={styles.logo}
              alt="logo"
            />
          </View>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>No Data Found</Text>
            <Text style={styles.emptyMessage}>
              There is no data available for the selected date range.
            </Text>
            <Text style={styles.emptySubMessage}>
              Please try selecting a different date range or check your filters.
            </Text>
          </View>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {weeklyGroupedData?.map((weeklyData, index) => {
        return (
          <React.Fragment key={index}>
            <Page1
              weeklyData={weeklyData}
              dateRange={dateRange}
              allSignature={allSignature}
              imageMap={imageMap}
            />
            <Page2
              weeklyData={weeklyData}
              dateRange={dateRange}
              allSignature={allSignature}
              imageMap={imageMap}
            />
          </React.Fragment>
        );
      })}
    </Document>
  );
};
